<template>
  <LoadingIndicator v-if="loading" message="Loading hat..." />
  <div v-else-if="loadError" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">{{ loadError }}</div>
  <div v-else class="space-y-6">
    <HermesRecordHeader :back="{ name: 'AdminHermesHats' }" back-label="All hats" :title="isNew ? 'New hat' : hat.label" :meta="isNew ? '' : `${hat.name} · ${FAMILY_LABELS[hat.family]} hat · updated ${absolute(hat.updated_at)}`">
      <template v-if="!isNew" #badges><HermesBadge :value="hat.is_active ? 'active' : 'inactive'" :label="hat.is_active ? 'Active' : 'Off'" /></template>
      <template v-if="superuser && !isNew && !editing" #actions>
        <button type="button" :class="TEXT_BUTTON" :disabled="saving" @click="toggleActive">{{ hat.is_active ? 'Turn off' : 'Turn on' }}</button>
        <button type="button" :class="SECONDARY_BUTTON" @click="startEdit()">Edit</button>
      </template>
    </HermesRecordHeader>
    <div v-if="!superuser" class="rounded-md bg-neutral-50 px-4 py-3 text-gray-700">Only Kairon staff can edit hats, because a change applies to the chat agent on every tenant.</div>
    <div v-if="actionError" role="alert" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">{{ actionError }}</div>

    <div class="grid grid-cols-12 gap-6 items-start">
      <div class="col-span-8 space-y-6">
        <HermesCard :title="editing ? (isNew ? 'New hat' : 'Edit hat') : 'Hat'">
          <template v-if="editing">
            <div class="grid grid-cols-2 gap-4 mb-4">
              <label v-if="isNew" class="block">
                <span class="block text-gray-500 mb-2">Name · lowercase, no spaces; cannot change later</span>
                <input v-model.trim="form.name" :class="INPUT" placeholder="e.g. readmissions">
              </label>
              <label class="block">
                <span class="block text-gray-500 mb-2">Label</span>
                <input v-model="form.label" :class="INPUT">
              </label>
              <div>
                <span class="block text-gray-500 mb-2">Family</span>
                <div class="inline-flex rounded-md border border-gray-300 overflow-hidden">
                  <button v-for="f in ['subject', 'activity']" :key="f" type="button" :class="['px-4 py-2', form.family === f ? 'bg-neutral-100 text-gray-900' : 'bg-white text-gray-600']" @click="form.family = f">{{ FAMILY_LABELS[f] }}</button>
                </div>
              </div>
              <label class="block">
                <span class="block text-gray-500 mb-2">Sort order</span>
                <input v-model.number="form.sort_order" type="number" :class="[SIZED_INPUT, 'w-32']">
              </label>
            </div>
            <label class="block mb-4">
              <span class="block text-gray-500 mb-2">Description · one or two sentences shown to the agent and in the chat</span>
              <textarea v-model="form.description" rows="3" :class="INPUT" />
            </label>
            <label class="block mb-4">
              <span class="block text-gray-500 mb-2">Instructions · what the agent should do while wearing this hat</span>
              <textarea v-model="form.instructions" rows="18" :class="[INPUT, 'font-mono text-[12px]']" />
            </label>
            <label class="block mb-4">
              <span class="block text-gray-500 mb-2">Pitfalls · mistakes to avoid</span>
              <textarea v-model="form.pitfalls" rows="10" :class="[INPUT, 'font-mono text-[12px]']" />
            </label>
            <section>
              <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Leads to</h3>
              <div v-for="(l, i) in form.leads_to" :key="i" class="flex gap-3 mb-2 items-start">
                <select v-model="l.hat" :class="[SIZED_INPUT, 'w-56']">
                  <option value="" disabled>Choose a hat</option>
                  <option v-for="o in otherHats" :key="o.name" :value="o.name">{{ o.label }}</option>
                </select>
                <input v-model="l.when" :class="INPUT" placeholder="when the conversation turns to…">
                <button type="button" :class="TEXT_BUTTON" @click="form.leads_to.splice(i, 1)">Remove</button>
              </div>
              <button type="button" :class="TEXT_BUTTON" @click="form.leads_to.push({ hat: '', when: '' })">Add next hat</button>
            </section>
          </template>
          <template v-else>
            <p class="text-gray-900 mb-4">{{ hat.description }}</p>
            <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Instructions</h3>
            <pre class="whitespace-pre-wrap font-sans text-gray-900 mb-4">{{ hat.instructions || '—' }}</pre>
            <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Pitfalls</h3>
            <pre class="whitespace-pre-wrap font-sans text-gray-900 mb-4">{{ hat.pitfalls || '—' }}</pre>
            <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Leads to</h3>
            <p v-if="!(hat.leads_to || []).length" class="text-gray-500">No next hat.</p>
            <ul v-else class="space-y-1">
              <li v-for="l in hat.leads_to" :key="l.hat">
                <router-link :to="{ name: 'AdminHermesHat', params: { hatName: l.hat } }" class="font-medium text-neutral-800">{{ labelOf(l.hat) }}</router-link>
                <span class="text-gray-600"> when {{ l.when }}</span>
              </li>
            </ul>
          </template>
        </HermesCard>
      </div>

      <div class="col-span-4 space-y-6">
        <HermesCard title="Switches on when">
          <section v-for="k in WHEN_KEYS" :key="k" class="mb-4 last:mb-0">
            <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-1">{{ WHEN_LABELS[k] }}</h3>
            <p class="text-gray-500 mb-2">{{ WHEN_HELP[k] }}</p>
            <div>
              <span v-for="(item, i) in whenOf(k)" :key="item" class="inline-flex items-center gap-1 rounded bg-gray-100 px-2 mr-1 mb-1">
                {{ item }}
                <button v-if="editing" type="button" class="text-gray-500 hover:text-gray-900" :aria-label="`Remove ${item}`" @click="form.wearing_when[k].splice(i, 1)">×</button>
              </span>
              <span v-if="!whenOf(k).length" class="text-gray-500">None</span>
            </div>
            <input v-if="editing" v-model="drafts[k]" :class="[INPUT, 'mt-2']" :placeholder="`Add ${k === 'words' ? 'a word' : k === 'tables' ? 'SCHEMA.TABLE' : 'a tool name'}, then Enter`" @keydown.enter.prevent="addWhen(k)">
          </section>
        </HermesCard>
        <HermesCard v-if="!isNew" title="History" :meta="versions.length ? `${versions.length} versions` : ''" :height="420">
          <p v-if="!versions.length" class="text-gray-500">No history yet.</p>
          <ul v-else class="divide-y divide-gray-100">
            <li v-for="(v, i) in versions" :key="v.version" class="py-2">
              <div class="flex items-center gap-2">
                <span class="text-gray-900">{{ absolute(v.at) }}</span>
                <span class="text-gray-500">{{ v.by || 'migration' }}</span>
                <button v-if="superuser && i > 0" type="button" :class="[TEXT_BUTTON, 'ml-auto']" @click="startEdit(v.hat)">Restore</button>
              </div>
              <div class="text-gray-600">{{ changeSummary(i) }}</div>
            </li>
          </ul>
        </HermesCard>
      </div>
    </div>

    <div v-if="editing" class="sticky bottom-0 z-10 -mx-8 border-t border-gray-200 bg-white px-8 py-3 flex items-center gap-4">
      <div v-if="blocker || saveError" role="alert" class="rounded-md bg-red-tint px-3 py-2 text-gray-900">{{ blocker || saveError }}</div>
      <span v-else class="text-gray-500">Saved changes reach the chat agent on its next message.</span>
      <div class="ml-auto flex gap-3">
        <button type="button" :class="TEXT_BUTTON" @click="cancel">Cancel</button>
        <button type="button" :class="PRIMARY_BUTTON" :disabled="!!blocker || saving" @click="save">{{ saving ? 'Saving…' : 'Save hat' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import HermesBadge from './HermesBadge.vue';
import HermesCard from './HermesCard.vue';
import HermesRecordHeader from './HermesRecordHeader.vue';
import { API, INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, SIZED_INPUT, TEXT_BUTTON, absolute, errorOf } from './hermes';

const WHEN_KEYS = ['words', 'tables', 'tools'];
const WHEN_LABELS = { words: 'Words in a message', tables: 'Tables a tool call touches', tools: 'Tools the agent calls' };
const WHEN_HELP = {
  words: 'A user message containing one of these puts the hat on.',
  tables: 'A tool call that names two or more of these puts the hat on.',
  tools: 'Calling one of these tools puts the hat on.',
};
const FAMILY_LABELS = { subject: 'Subject', activity: 'Activity' };
const FIELDS = ['family', 'label', 'description', 'wearing_when', 'instructions', 'pitfalls', 'leads_to', 'is_active', 'sort_order'];
const FIELD_WORDS = { wearing_when: 'switch-on rules', leads_to: 'next hats', is_active: 'active', sort_order: 'sort order' };

function blankHat() {
  return { name: '', family: 'subject', label: '', description: '', instructions: '', pitfalls: '', sort_order: 0,
    wearing_when: { words: [], tables: [], tools: [] }, leads_to: [], is_active: true };
}

function copyOf(hat) {
  const when = hat.wearing_when || {};
  return {
    ...JSON.parse(JSON.stringify(hat)),
    wearing_when: Object.fromEntries(WHEN_KEYS.map((k) => [k, [...(when[k] || [])]])),
    leads_to: (hat.leads_to || []).map((l) => ({ ...l })),
  };
}

export default {
  name: 'HermesHatEditor',
  components: { HermesBadge, HermesCard, HermesRecordHeader, LoadingIndicator },
  data() {
    return {
      hat: null, hats: [], versions: [], form: null, drafts: { words: '', tables: '', tools: '' },
      loading: true, loadError: '', editing: false, saving: false, saveError: '', actionError: '',
      WHEN_KEYS, WHEN_LABELS, WHEN_HELP, FAMILY_LABELS,
      INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, SIZED_INPUT, TEXT_BUTTON,
    };
  },
  computed: {
    isNew() {
      return this.$route.params.hatName === 'new';
    },
    superuser() {
      return !!this.$store.getters['auth/current_user']?.is_superuser;
    },
    otherHats() {
      const own = (this.form || {}).name || (this.hat || {}).name;
      return this.hats.filter((h) => h.name !== own);
    },
    blocker() {
      const f = this.form;
      if (!f) return '';
      if (this.isNew && !/^[a-z0-9_-]+$/.test(f.name || '')) return 'Give the hat a name: lowercase letters, numbers, - or _.';
      if (this.isNew && f.name === 'new') return 'A hat cannot be called new.';
      if (!f.label.trim()) return 'Give the hat a label.';
      if (f.leads_to.some((l) => !l.hat || !l.when.trim())) return 'Each next hat needs a hat and a when.';
      return '';
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    absolute,
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        const name = this.$route.params.hatName;
        const [all, one, versions] = await Promise.all([
          this.$axios.get(`${API}/hats/`),
          this.isNew ? Promise.resolve({ data: blankHat() }) : this.$axios.get(`${API}/hats/${name}/`),
          this.isNew ? Promise.resolve({ data: [] }) : this.$axios.get(`${API}/hats/${name}/versions/`),
        ]);
        this.hats = all.data;
        this.hat = one.data;
        this.versions = versions.data;
        if (this.isNew) this.startEdit();
      } catch (e) {
        this.loadError = errorOf(e, 'This hat could not be loaded.');
      } finally {
        this.loading = false;
      }
    },
    startEdit(from) {
      this.form = copyOf({ ...this.hat, ...(from || {}) });
      this.saveError = '';
      this.editing = true;
    },
    cancel() {
      if (this.isNew) {
        this.$router.push({ name: 'AdminHermesHats' });
        return;
      }
      this.editing = false;
      this.form = null;
    },
    whenOf(k) {
      const source = this.editing ? this.form : this.hat;
      return ((source && source.wearing_when) || {})[k] || [];
    },
    addWhen(k) {
      const value = (this.drafts[k] || '').trim();
      if (value && !this.form.wearing_when[k].includes(value)) this.form.wearing_when[k].push(value);
      this.drafts[k] = '';
    },
    labelOf(name) {
      return (this.hats.find((h) => h.name === name) || {}).label || name;
    },
    changeSummary(i) {
      const v = this.versions[i];
      const before = this.versions[i + 1];
      if (!before) return v.change === '+' ? 'Created' : 'Earliest version';
      const changed = FIELDS.filter((f) => JSON.stringify(v.hat[f]) !== JSON.stringify(before.hat[f]));
      return changed.length ? `Changed ${changed.map((f) => FIELD_WORDS[f] || f).join(', ')}` : 'No field changes';
    },
    payload() {
      const f = this.form;
      const body = {
        family: f.family, label: f.label.trim(), description: f.description, instructions: f.instructions,
        pitfalls: f.pitfalls, sort_order: Number(f.sort_order) || 0, is_active: f.is_active,
        wearing_when: Object.fromEntries(WHEN_KEYS.filter((k) => f.wearing_when[k].length).map((k) => [k, f.wearing_when[k]])),
        leads_to: f.leads_to.map((l) => ({ hat: l.hat, when: l.when.trim() })),
      };
      if (this.isNew) body.name = f.name;
      return body;
    },
    async save() {
      this.saving = true;
      this.saveError = '';
      try {
        const { data } = this.isNew
          ? await this.$axios.post(`${API}/hats/`, this.payload())
          : await this.$axios.patch(`${API}/hats/${this.hat.name}/`, this.payload());
        if (this.isNew) {
          this.$router.replace({ name: 'AdminHermesHat', params: { hatName: data.name } });
          return;
        }
        this.editing = false;
        this.form = null;
        await this.load();
      } catch (e) {
        this.saveError = errorOf(e, 'The hat could not be saved.');
      } finally {
        this.saving = false;
      }
    },
    async toggleActive() {
      this.saving = true;
      this.actionError = '';
      try {
        await this.$axios.patch(`${API}/hats/${this.hat.name}/`, { is_active: !this.hat.is_active });
        await this.load();
      } catch (e) {
        this.actionError = errorOf(e, 'The hat could not be changed.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
